import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from '../../shared/services/auth.service';
import { ApiService } from '../../shared/services/api.service';

@Injectable({
	providedIn: 'root',
})
export class AuthGuard implements CanActivate {
	constructor(private router: Router, private auth: AuthService, private api: ApiService) {}

	canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
		const tokenFromUrl = next.queryParams?.['token'];

		// If a token is present in URL, validate it before proceeding
		if (tokenFromUrl) {
			if (this.auth.user()) {
				this.auth.logout();
			}

			return this.api.validateAuthToken(tokenFromUrl).pipe(
				map((res: any) => {
					if (res && res['status'] === '1') {
						const user = {
							...res.response,
							token: tokenFromUrl,
							userType: res?.response?.userType === '' ? '0' : res?.response?.userType,
						};
						this.auth.loginV2(user);
					}

					// Build redirect URL (original route without token)
					const currentUrl = state.url.split('?')[0]; // e.g. "/user/wa-alerts"
					this.router.navigate([currentUrl], { replaceUrl: true }); // removes token param cleanly
					return false; // prevent double navigation
				}),
				catchError(() => {
					// On failure, ensure token is removed and go to login preserving redirect
					const redirectUrl = state.url.split('?')[0];
					this.router.navigate(['/login'], {
						queryParams: {
							redirect: redirectUrl,
						},
						replaceUrl: true,
					});
					return of(false);
				})
			);
		}

		// Handle normal login/logout flow
		if (state.url.includes('/login')) {
			return this.auth.user() ? this.router.createUrlTree(['/']) : true;
		} else {
			if (this.auth.user()) {
				return true;
			} else {
				const path = state.url.split('?')[0];
				return this.router.createUrlTree(['/login'], {
					queryParams: { redirect: path },
				});
			}
		}
	}
}
